export type DeployEnvironment = 'development' | 'staging' | 'production';

export type FaviconVariant = 'dev' | 'staging' | 'prod';

const FAVICON_LINK_IDS = {
  icon: 'nq-favicon',
  icon32: 'nq-favicon-32',
  appleTouch: 'nq-apple-touch-icon',
} as const;

export function getDeployEnvironment(): DeployEnvironment {
  if (import.meta.env.DEV) {
    return 'development';
  }

  const deployEnv =
    import.meta.env.NQ_DEPLOY_ENV ?? import.meta.env.VITE_DEPLOY_ENV;
  if (deployEnv === 'staging') {
    return 'staging';
  }

  return 'production';
}

export function getFaviconVariant(
  deployEnvironment: DeployEnvironment = getDeployEnvironment(),
): FaviconVariant {
  switch (deployEnvironment) {
    case 'development':
      return 'dev';
    case 'staging':
      return 'staging';
    default:
      return 'prod';
  }
}

export function getFaviconHref(variant: FaviconVariant, size?: '32' | '180'): string {
  if (size === '32') {
    return `/favicon-${variant}-32x32.png`;
  }
  if (size === '180') {
    return `/apple-touch-icon-${variant}.png`;
  }
  return `/favicon-${variant}.png`;
}

function upsertLink(
  rel: string,
  href: string,
  id: string,
  sizes?: string,
): void {
  let link = document.getElementById(id) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.id = id;
    link.rel = rel;
    document.head.appendChild(link);
  }
  link.href = href;
  if (sizes) {
    link.sizes = sizes;
  } else {
    link.removeAttribute('sizes');
  }
}

export function applyFavicon(
  deployEnvironment: DeployEnvironment = getDeployEnvironment(),
): void {
  const variant = getFaviconVariant(deployEnvironment);

  upsertLink('icon', getFaviconHref(variant), FAVICON_LINK_IDS.icon);
  upsertLink(
    'icon',
    getFaviconHref(variant, '32'),
    FAVICON_LINK_IDS.icon32,
    '32x32',
  );
  upsertLink(
    'apple-touch-icon',
    getFaviconHref(variant, '180'),
    FAVICON_LINK_IDS.appleTouch,
    '180x180',
  );
}

export function formatPageTitle(prefix: string, pageTitle: string): string {
  return `${prefix}: ${pageTitle}`;
}

export type RouteTitleRule = {
  path: string;
  title: string;
};

export function resolveRouteTitle(
  pathname: string,
  rules: RouteTitleRule[],
  fallback: string,
): string {
  for (const rule of rules) {
    if (rule.path.endsWith('*')) {
      const base = rule.path.slice(0, -1);
      if (pathname.startsWith(base)) {
        return rule.title;
      }
      continue;
    }

    if (rule.path.endsWith('/') && pathname.startsWith(rule.path)) {
      return rule.title;
    }

    if (pathname === rule.path) {
      return rule.title;
    }
  }

  return fallback;
}
